import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { eventBannerThumb, photoThumb } from '../utils/media';

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

// Stands in for a vehicle result that has no photo yet.
const CarIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 17h14M3 13l1.8-5.1A2 2 0 0 1 6.7 6.5h10.6a2 2 0 0 1 1.9 1.4L21 13v4a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-1H6v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
    <circle cx="7.5" cy="13.5" r="1" /><circle cx="16.5" cy="13.5" r="1" />
  </svg>
);

const UserResult = ({ user }) => (
  <Link to={`/profile/${user.username}`} className="search-result-item">
    <div className="search-result-avatar">
      {user.profileImageUrl
        ? <img src={user.profileImageUrl} alt="" />
        : <span>{user.username?.[0]?.toUpperCase()}</span>}
    </div>
    <div className="search-result-info">
      <div className="search-result-name">{user.username}</div>
      <div className="search-result-sub">
        {[user.firstName, user.lastName].filter(Boolean).join(' ')}
        {user.vehicleCount != null && (
          <span className="search-result-meta"> · {user.vehicleCount} {user.vehicleCount === 1 ? 'car' : 'cars'}</span>
        )}
      </div>
    </div>
    <div className="search-result-arrow">›</div>
  </Link>
);

const VehicleResult = ({ item }) => {
  const v = item.vehicle;
  const owner = item.owner;
  const img = photoThumb(item.images?.[0]);
  return (
    <Link to={`/vehicle/${item.id}`} className="search-result-item">
      <div className="search-result-thumb">
        {img
          ? <img src={img} alt="" />
          : <span className="search-result-thumb-placeholder"><CarIcon /></span>}
      </div>
      <div className="search-result-info">
        <div className="search-result-name">{v?.year} {v?.make} {v?.model}</div>
        <div className="search-result-sub">
          {owner?.username && <span>by @{owner.username}</span>}
          {item.marketValue && <span className="search-result-meta"> · ${Number(item.marketValue).toLocaleString()}</span>}
        </div>
      </div>
      <div className="search-result-arrow">›</div>
    </Link>
  );
};

const CalendarGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const EventResult = ({ event }) => {
  const tags = (event.tags && event.tags.length)
    ? event.tags
    : (event.category ? [event.category] : []);
  const dateLabel = event.eventDate
    ? new Date(event.eventDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Date TBD';
  const loc = event.suburb || event.address || null;
  return (
    <Link to={`/events?event=${event.id}`} className="search-result-item">
      <div className="search-result-thumb">
        {event.bannerImageUrl
          ? <img src={eventBannerThumb(event)} alt="" />
          : <span className="search-result-thumb-placeholder"><CalendarGlyph /></span>}
      </div>
      <div className="search-result-info">
        <div className="search-result-name">{event.title}</div>
        <div className="search-result-sub">
          <span>{dateLabel}</span>
          {loc && <span className="search-result-meta"> · {loc}</span>}
          {tags.length > 0 && <span className="search-result-meta"> · {tags.slice(0, 2).join(', ')}</span>}
        </div>
      </div>
      <div className="search-result-arrow">›</div>
    </Link>
  );
};

const TABS = [
  { key: 'person', label: 'People' },
  { key: 'make', label: 'Vehicles' },
  { key: 'event', label: 'Events' },
];

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get('q') || '';
  const [query, setQuery] = useState(initialQ);
  const [activeTab, setActiveTab] = useState('person');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(!!initialQ);
  const inputRef = useRef();
  const debounceRef = useRef();

  const doSearch = useCallback(async (q, type) => {
    if (!q.trim()) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await api.get('/discover/search', { params: { q: q.trim(), type, limit: 20 } });
      setResults(data.results || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchParams(query ? { q: query } : {}, { replace: true });
      doSearch(query, activeTab);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, activeTab, doSearch, setSearchParams]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setResults([]);
    if (query.trim()) doSearch(query, tab);
  };

  return (
    <div className="search-page">
      <div className="search-page-inner">
        <div className="search-page-bar">
          <span className="search-page-icon"><SearchIcon /></span>
          <input
            ref={inputRef}
            type="text"
            className="search-page-input"
            placeholder="Search usernames, collections, events, etc..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          {query && (
            <button className="search-page-clear" onClick={() => { setQuery(''); setResults([]); setSearched(false); inputRef.current?.focus(); }} aria-label="Clear">
              ✕
            </button>
          )}
        </div>

        <div className="tabs search-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              className={`tab${activeTab === t.key ? ' active' : ''}`}
              onClick={() => handleTabChange(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="search-page-results">
          {loading && (
            <div className="search-page-state">
              <div className="loading-spinner" style={{ width: 28, height: 28, borderWidth: 2 }} />
            </div>
          )}

          {!loading && !searched && (
            <div className="search-page-state search-page-hint">
              <span className="search-hint-icon"><SearchIcon /></span>
              <p>Search for people, vehicles, collections, and more</p>
            </div>
          )}

          {!loading && searched && results.length === 0 && (
            <div className="search-page-state">
              <p className="search-no-results">No results for <strong>"{query}"</strong></p>
              <p className="search-no-results-sub">Try a different spelling or search term</p>
            </div>
          )}

          {!loading && results.length > 0 && (
            <div className="search-results-list">
              {activeTab === 'person'
                ? results.map(u => <UserResult key={u.id} user={u} />)
                : activeTab === 'make'
                  ? results.map(v => <VehicleResult key={v.id} item={v} />)
                  : results.map(ev => <EventResult key={ev.id} event={ev} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Search;
